import { Venue, VenuePinType } from "../types/venue";

// A venue with any user-contributed data at all: an approved review, a free-text
// experience, a live alert, a general-user photo (auto-approved on upload, no tag
// required), or a general-user tag — regardless of who added it (see
// hasVerifiedData for the stricter team-tagged signal that grants the blue tick).
// Drives the orange pin and the "Sociability Places" map filter.
export const hasVenueData = (
  venue: Pick<
    Venue,
    | "reviewsDetailsSubmitted"
    | "hasExperiences"
    | "hasAlerts"
    | "hasGeneralUserPhotos"
    | "hasGeneralUserTags"
  >,
): boolean =>
  (venue.reviewsDetailsSubmitted ?? 0) > 0 ||
  !!venue.hasExperiences ||
  !!venue.hasAlerts ||
  !!venue.hasGeneralUserPhotos ||
  !!venue.hasGeneralUserTags;

/**
 * Provenance badge on a venue card. A staff verification outranks everything;
 * below that, general-user tags outrank team tags, so "Community Contributed"
 * means the venue is unverified and users have tagged it. Venues with no data at
 * all get no badge.
 */
export type VenueDataSource = "team" | "user" | "none";

/**
 * `business_verified` marking a venue as checked by the team. Only this value
 * counts — "Owner Verified" and "User Verified" are different states and leave
 * provenance to the tag flags below.
 *
 * Mirrors GoogleBusiness::VERIFIED_SOCIABILITY on the backend. Compared against
 * the raw field rather than the localized label: this is a backend enum that
 * arrives verbatim in every locale.
 */
const VERIFIED_SOCIABILITY = "Sociability Verified";

export const getVenueDataSource = (
  venue: Pick<
    Venue,
    | "reviewsDetailsSubmitted"
    | "hasExperiences"
    | "hasAlerts"
    | "hasTeamTags"
    | "hasGeneralUserTags"
    | "hasGeneralUserPhotos"
  > & {
    venueDataSource?: VenueDataSource;
    businessVerified?: string | null;
  },
): VenueDataSource => {
  // The backend resolves this; the rest is a fallback for payloads that predate
  // the field, and for the stale/thin copies native's map cache can hold.
  if (venue.venueDataSource) return venue.venueDataSource;

  // Rule order below mirrors AzureSearchService::resolveVenueDataSource exactly.
  // Miss this first rule — as the fallback used to — and a staff-verified venue
  // whose tags came from general users resolves "user" here while the backend
  // says "team", so the map card contradicts the details screen.
  if (venue.businessVerified === VERIFIED_SOCIABILITY) return "team";

  if (venue.hasGeneralUserTags) return "user";
  if (venue.hasTeamTags) return "team";
  return hasVenueData(venue) ? "user" : "none";
};

/**
 * "Sociability Verified" blue tick on a map pin. Derived from the card badge so the two
 * can't disagree — reading it off reviewsDetailsSubmitted gave the tick to
 * community-contributed venues too. Experiences and alerts never earn it: team members
 * use regular accounts for those.
 */
export const hasVerifiedData = (
  // Exactly what the resolver reads, so the two can't drift: this used to list a
  // narrower Pick that left out the fields actually deciding the answer
  // (venueDataSource, hasGeneralUserTags, businessVerified), which let a caller
  // hand over an object that resolved wrong with nothing to flag it.
  venue: Parameters<typeof getVenueDataSource>[0],
): boolean => getVenueDataSource(venue) === "team";

export const getPinIcon = (business: Venue) => {
  // Priority order matches native implementation:
  // GREEN > BLACK > BLUE > PURPLE > ORANGE > GREY

  // 1. Return to venue (highest priority)
  if (business.isReturnToVenue) {
    return VenuePinType.Green;
  }

  // 2. Note status pending
  if (business.isNoteStatusPending) {
    return VenuePinType.Black;
  }

  // 3. Has notes for mappers
  if (business.hasNotesForMappers) {
    return VenuePinType.Blue;
  }

  // 4. To be verified
  if (business.to_be_verified === 1) {
    return VenuePinType.Purple;
  }

  // 5. Mapped venues (has any user-contributed data)
  if (hasVenueData(business)) {
    return VenuePinType.Orange;
  }

  // 6. Unmapped venues (default)
  return VenuePinType.Grey;
};

// Canonical analytics `pin_type` for each map-pin colour (tracking-data vocabulary). Black ("note
// status pending") has no exact bucket -> venue_with_notes. Keep in step with the mapper category
// comments in useDesktopHomePageMapperVenues.
const PIN_TYPE_BY_COLOR: Record<VenuePinType, string> = {
  [VenuePinType.Green]: "return_to_venue",
  [VenuePinType.Black]: "venue_with_notes",
  [VenuePinType.Blue]: "venue_with_notes",
  [VenuePinType.Purple]: "awaiting_verification",
  [VenuePinType.Orange]: "mapped_places_near_you",
  [VenuePinType.Grey]: "be_first_to_add",
};

/** Canonical analytics `pin_type` for a home/search `Venue` — drives off the same getPinIcon chain. */
export const pinTypeForVenue = (venue: Venue): string =>
  PIN_TYPE_BY_COLOR[getPinIcon(venue)] ?? "be_first_to_add";

// Mirrors PlaceTypeMapper::GENERIC_TYPES on the backend — returned for almost
// every place, so never a usable label.
const GENERIC_GOOGLE_TYPES = new Set([
  "point_of_interest",
  "establishment",
  "premise",
  "geocode",
]);

/**
 * Last-resort category label for a venue we haven't classified into a
 * Sociability business type (empty business_type/calcVenueType — e.g.
 * museums, galleries, and other categories outside cafés/restaurants/bars/
 * bakeries). Falls back to Google's own place type, humanized, so the chip
 * still reads as something the user can relate to rather than a bare "Venue"
 * — the "this category isn't supported yet" messaging elsewhere is unchanged.
 */
export const firstGoogleTypeLabel = (types?: string | null): string | null => {
  if (!types) return null;

  const token = types
    .split("|")
    .map((t) => t.trim().toLowerCase())
    .find((t) => t.length > 0 && !GENERIC_GOOGLE_TYPES.has(t));

  if (!token) return null;

  return token
    .split("_")
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(" ");
};

/** True for the backend's generic placeholder category ("Venue") — not a
 * real classification, so callers should treat it the same as empty/null. */
export const isPlaceholderCategoryLabel = (value?: string | null): boolean =>
  !value || value.trim().toLowerCase() === "venue";

// The fields a venue-DETAIL payload exposes that map onto the getPinIcon chain. Structural (not the
// full VenueDetails type) so callers can pass the detail object without a tight import coupling.
// Detail payloads spell the review count singular, a Venue spells it plural — both are accepted so
// the helper stays correct for the call sites that hand it a Venue.
type VenueDetailPinSignals = {
  isReturnToVenue?: number | null;
  isNoteStatusPending?: number | null;
  to_be_verified?: number | null;
  businessNotesForMappers?: unknown[] | null;
  hasNotesForMappers?: unknown;
  reviewDetailsSubmitted?: number | null;
  reviewsDetailsSubmitted?: number | null;
  hasExperiences?: boolean | null;
  hasAlerts?: boolean | null;
  hasGeneralUserPhotos?: boolean | null;
  hasGeneralUserTags?: boolean | null;
};

/**
 * Canonical analytics `pin_type` for a venue-DETAIL response, mirroring getPinIcon's priority
 * (GREEN>BLACK>BLUE>PURPLE>ORANGE>GREY) off the fields a detail payload exposes.
 * @param v venue detail payload, or any object carrying the same pin signals
 * @returns tracking-plan pin_type token
 */
export const pinTypeForVenueDetails = (
  v?: VenueDetailPinSignals | null,
): string => {
  if (v?.isReturnToVenue) return "return_to_venue";
  if (v?.isNoteStatusPending) return "venue_with_notes";
  if (v?.hasNotesForMappers) return "venue_with_notes";
  if ((v?.businessNotesForMappers?.length ?? 0) > 0) return "venue_with_notes";
  if (v?.to_be_verified === 1) return "awaiting_verification";
  // "Mapped" has to be the same signal that turns the pin orange. Keying off the approved
  // free-text review list misses every tag-, photo- or alert-only venue (i.e. most mapped ones).
  if (
    hasVenueData({
      reviewsDetailsSubmitted:
        v?.reviewsDetailsSubmitted ?? v?.reviewDetailsSubmitted ?? 0,
      hasExperiences: v?.hasExperiences ?? false,
      hasAlerts: v?.hasAlerts ?? false,
      hasGeneralUserPhotos: v?.hasGeneralUserPhotos ?? false,
      hasGeneralUserTags: v?.hasGeneralUserTags ?? false,
    })
  ) {
    return "mapped_places_near_you";
  }
  return "be_first_to_add";
};
